// Global chat client state. One module singleton feeding both mounts
// (hub panel + social drawer tab). Messages arrive over the socket;
// the tail is re-fetched on every (re)connect since the server keeps
// only an in-memory ring buffer and a reconnect may have skipped frames.
//
// Unread math: `unread` counts messages that landed while no mount was
// visible; `mentions` counts pings (an @mention or a reply to you) the
// same way. Only `mentions` reaches the social-hub icon badge, so the
// icon doesn't pulse on every line of small talk.
import { api, on, send, onConnectionState } from './serverClient';
import * as onlinePlayers from './arena/onlinePlayersStore';

export const MAX_MESSAGES = 200;

let state = { messages: [], unread: 0, mentions: 0, onlineCount: 0, hydrated: false, error: null, waitUntil: null, hasMore: true, loadingOlder: false };
const OLDER_PAGE = 50;
const listeners = new Set();
let visibleMounts = 0;
let started = false;
let currentPlayerId = null;
let unsubs = [];

function emit() {
  for (const l of listeners) {
    try { l(state); } catch (err) { console.error('[globalChat] listener error:', err); }
  }
}

function patch(next) {
  state = { ...state, ...next };
  emit();
}

export function getState() {
  return state;
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export async function hydrate() {
  try {
    const res = await api.get(`/chat/global?limit=${MAX_MESSAGES}`);
    patch({
      messages: Array.isArray(res?.messages) ? res.messages : [],
      onlineCount: res?.onlineCount || 0,
      hydrated: true,
    });
  } catch (err) {
    console.warn('[globalChat] hydrate failed:', err?.message || err);
  }
}

export function setSelf(playerId) {
  currentPlayerId = playerId || null;
}

// Older page for infinite scroll. Prepends, dedupes against what the
// socket may have delivered meanwhile, and latches hasMore off on a short
// page so the view stops asking.
export async function loadOlder() {
  const first = state.messages[0];
  if (!first || state.loadingOlder || !state.hasMore) return 0;
  patch({ loadingOlder: true });
  try {
    const res = await api.get(`/chat/global?before=${encodeURIComponent(first.id)}&limit=${OLDER_PAGE}`);
    const older = Array.isArray(res?.messages) ? res.messages : [];
    const known = new Set(state.messages.map((m) => m.id));
    const fresh = older.filter((m) => m?.id && !known.has(m.id));
    patch({ messages: [...fresh, ...state.messages], hasMore: older.length >= OLDER_PAGE, loadingOlder: false });
    return fresh.length;
  } catch (err) {
    console.warn('[globalChat] loadOlder failed:', err?.message || err);
    patch({ loadingOlder: false });
    return 0;
  }
}

export function start(playerId = null) {
  if (playerId) currentPlayerId = playerId;
  if (started) return;
  started = true;
  unsubs = [
    on('chat:global:message', (msg) => {
      if (!msg?.id) return;
      const messages = [...state.messages, msg].slice(-MAX_MESSAGES);
      patch({ messages, unread: visibleMounts > 0 ? 0 : state.unread + 1, error: null });
    }),
    on('chat:global:delete', (data) => {
      if (!data?.id) return;
      patch({ messages: state.messages.filter((m) => m.id !== data.id) });
    }),
    on('chat:global:edit', (data) => {
      if (!data?.id) return;
      patch({
        messages: state.messages.map((m) => {
          if (m.id === data.id) return { ...m, body: data.body, mentions: data.mentions || [], editedAt: data.editedAt || null };
          if (m.replyTo?.id === data.id) return { ...m, replyTo: { ...m.replyTo, bodyPreview: data.body.slice(0, 120) } };
          return m;
        }),
      });
    }),
    on('chat:global:reaction', (data) => {
      if (!data?.id || !data?.emoji) return;
      patch({
        messages: state.messages.map((m) => (m.id === data.id
          ? { ...m, reactions: applyReaction(m.reactions, data, currentPlayerId) }
          : m)),
      });
    }),
    on('chat:global:mention', () => {
      if (visibleMounts === 0) patch({ mentions: state.mentions + 1 });
    }),
    on('chat:global:error', (data) => patch({
      error: data?.error || 'Could not send.',
      // `wait` is seconds (rate limits), `until` an ISO time (mutes).
      waitUntil: data?.wait ? Date.now() + data.wait * 1000 : data?.until ? Date.parse(data.until) : null,
    })),
    onConnectionState((s) => { if (s === 'open') hydrate(); }),
    // Same rows the Hall's Online tab renders, so both panels show one number.
    onlinePlayers.subscribe(({ rows }) => patch({ onlineCount: rows.length })),
  ];
}

export function stop() {
  for (const u of unsubs) { try { u(); } catch {} }
  unsubs = [];
  started = false;
}

// `replay` is a shared match's card snapshot (ReplayShareCard meta); a
// message may be the card alone.
export function post(body, replyTo = null, replay = null) {
  const trimmed = (body || '').trim();
  if (!trimmed && !replay) return false;
  patch({ error: null });
  return send('chat:global:post', { body: trimmed, replyTo, ...(replay ? { replay } : {}) });
}

// A post from outside the composer (the replay viewer's share menu), where
// nothing is watching the store: settles on the server's echo of the card
// or on its refusal, so the caller can say which.
export function postReplayAndConfirm(replay, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    let offMessage = () => {};
    let offError = () => {};
    let timer = null;
    const settle = (fn, value) => { offMessage(); offError(); clearTimeout(timer); fn(value); };
    offMessage = on('chat:global:message', (msg) => {
      if (msg?.playerId === currentPlayerId && msg?.replay?.roomCode === replay?.roomCode) settle(resolve, msg);
    });
    offError = on('chat:global:error', (data) => settle(reject, new Error(data?.error || 'Could not send.')));
    timer = setTimeout(() => settle(reject, new Error('The Tavern did not answer.')), timeoutMs);
    if (!post('', null, replay)) settle(reject, new Error('Not connected.'));
  });
}

export function remove(id) {
  return send('chat:global:delete', { id });
}

export function edit(id, body) {
  return send('chat:global:edit', { id, body: (body || '').trim() });
}

export function react(id, emoji) {
  return send('chat:global:react', { id, emoji });
}

// One reaction broadcast folded into a message's chip list: the count is
// authoritative, `me` only moves when the viewer is the one who flipped
// it, and a chip whose count hit zero disappears.
export function applyReaction(reactions, data, me) {
  const list = Array.isArray(reactions) ? reactions : [];
  const mine = data.playerId === me;
  if (!data.count) return list.filter((r) => r.emoji !== data.emoji);
  if (!list.some((r) => r.emoji === data.emoji)) return [...list, { emoji: data.emoji, count: data.count, me: mine && data.reacted }];
  return list.map((r) => (r.emoji === data.emoji ? { ...r, count: data.count, me: mine ? data.reacted : r.me } : r));
}

export function clearError() {
  if (state.error) patch({ error: null, waitUntil: null });
}

// Mounts call this on mount/unmount. While at least one is visible,
// everything counts as read.
export function setVisible(visible) {
  visibleMounts = Math.max(0, visibleMounts + (visible ? 1 : -1));
  if (visibleMounts > 0 && (state.unread || state.mentions)) patch({ unread: 0, mentions: 0 });
}

export function _resetForTests() {
  stop();
  state = { messages: [], unread: 0, mentions: 0, onlineCount: 0, hydrated: false, error: null, waitUntil: null, hasMore: true, loadingOlder: false };
  listeners.clear();
  visibleMounts = 0;
  currentPlayerId = null;
}
